import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { ITicketFormData } from "app/containers/Features/containers/Boards/containers/Tickets/types";
import { ticketGroupsActions } from "app/containers/Features/containers/Boards/containers/Tickets/redux/slice";
import { TicketValueEnum } from "app/containers/Features/containers/Boards/types";
import { IBoardState } from "app/containers/Features/containers/Boards/components/Settings/types";

type DefaultTicketValueItem = IBoardState["defaultTicketValues"][number];
type DefaultFieldValue = string | number | boolean | string[] | Date;

const DEFAULT_VALUE_META_KEYS = ["_id", "id", "name", "data", "type", "value"];

const DEFAULT_KEY_TO_FORM_FIELD: Partial<Record<string, keyof ITicketFormData>> = {
  [TicketValueEnum.TICKET_NAME]: "title",
  [TicketValueEnum.TICKET_DESCRIPTION]: "description",
  [TicketValueEnum.STATUS]: "status",
  [TicketValueEnum.ASSIGNEE]: "assignedTo",
  [TicketValueEnum.DUE_DATE]: "dueDate",
  [TicketValueEnum.PRIORITY]: "priority",
  [TicketValueEnum.TAGS]: "tags",
};

const isEmptyValue = (value: ITicketFormData[keyof ITicketFormData] | string[] | Date) =>
  Array.isArray(value) ? value.length === 0 : !value;

const getDefaultEntry = (
  item: DefaultTicketValueItem,
): { key: string; value: DefaultFieldValue } | null => {
  const key = Object.keys(item).find((field) => !DEFAULT_VALUE_META_KEYS.includes(field));
  if (!key) return null;

  return {
    key,
    value: item[key as keyof DefaultTicketValueItem] as DefaultFieldValue,
  };
};

export const useApplyDefaultTicketValues = (
  defaultTicketValues: IBoardState["defaultTicketValues"] | undefined,
  form: ITicketFormData,
) => {
  const dispatch = useDispatch();
  const didApplyDefaults = useRef(false);

  useEffect(() => {
    if (didApplyDefaults.current) return;

    const defaults = defaultTicketValues || [];
    if (!defaults.length) return;

    defaults.forEach((item) => {
      const entry = getDefaultEntry(item);
      if (!entry) return;

      const { key, value } = entry;
      const formField = DEFAULT_KEY_TO_FORM_FIELD[key];

      if (formField) {
        const currentValue = form[formField];
        if (isEmptyValue(currentValue)) {
          dispatch(ticketGroupsActions.setTicketFormValue({ name: formField, value }));
        }
        return;
      }

      const currentCustomField = form.customFields?.find((field) => field.fieldId === key);
      if (!currentCustomField || isEmptyValue(currentCustomField.value)) {
        dispatch(
          ticketGroupsActions.setTicketFormCustomField({
            fieldId: key,
            value,
            error: "",
          }),
        );
      }
    });

    didApplyDefaults.current = true;
  }, [defaultTicketValues, dispatch, form]);
};
